import { useContext, useLayoutEffect, useState } from 'react';

import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Notice,
  TextLink,
} from '@upside/flux-ui';
import { LoginResponse } from '@upside/spa-shared-context/lib/authentication-context/AuthenticationContext';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { z } from 'zod';

import { AppContext } from '../../context/App';
import { useInitServiceCountryFromQuery } from '../../utils';
import { RegionSelect } from './RegionSelect';
import {
  MFADetails,
  RegisterState,
  ResetRequiredState,
  TotpDetails,
  TotpSetupDetails,
} from './types';

type LoginStep = 'email' | 'password';

export function LoginForm({
  timedOut,
  setRegisterState,
  setMfaState,
  setTotpState,
  setTotpSetupState,
  setResetRequiredState,
}: {
  timedOut: boolean;
  setMfaState: (mfaDetails: MFADetails) => void;
  setTotpState: (totpDetails: TotpDetails) => void;
  setTotpSetupState: (totpSetupDetails: TotpSetupDetails) => void;
  setRegisterState: (registerDetails: RegisterState) => void;
  setResetRequiredState: (resetRequiredState: ResetRequiredState) => void;
}) {
  const context = useContext(AppContext);
  useInitServiceCountryFromQuery(context);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState<LoginStep>('email');
  const [showPassword, setShowPassword] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [passwordStepSubmitted, setPasswordStepSubmitted] = useState(false);
  const [hasSsoError, setHasSsoError] = useState(false);

  useLayoutEffect(() => {
    if (searchParams.get('ssoError') === 'true') {
      setHasSsoError(true);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('ssoError');
      const newUrl =
        location.pathname +
        (newParams.toString() ? `?${newParams}` : '') +
        location.hash;
      navigate(newUrl, { replace: true });
    }
  }, [searchParams, location.pathname, location.hash, navigate]);

  const { t: translate } = useTranslation();

  const formSchema = z.object({
    username: z
      .string({
        error: translate('errorProvideEmail'),
      })
      .min(1, translate('errorProvideEmail'))
      .email(translate('errorIncorrectEmailFormat')),
    password: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      username: '',
      password: '',
    },
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const { formState, setError } = form;

  const { isSubmitting, errors } = formState;

  const handleEmailStep = async (email: string) => {
    setIsDiscovering(true);

    try {
      const codeChallenge =
        await context?.authentication.generateAuthChallenge?.();
      const authorizeUrl = await context?.authentication.ssoDiscoverEmail?.(
        email,
        codeChallenge,
      );

      if (authorizeUrl) {
        const redirectTo = searchParams.get('redirectTo');
        if (redirectTo) {
          sessionStorage.setItem('appRedirectTo', redirectTo);
        }
        window.location.href = authorizeUrl;
        return;
      }

      context?.authentication.codeVerifierStorage.clearCodeVerifier();
      setIsDiscovering(false);
      form.resetField('password');
      setStep('password');
    } catch {
      context?.authentication.codeVerifierStorage.clearCodeVerifier();
      setIsDiscovering(false);
      setError('root', {
        message: translate('errorSsoDiscovery'),
      });
    }
  };

  const handlePasswordStep = async (data: z.infer<typeof formSchema>) => {
    const { username, password } = data;

    setPasswordStepSubmitted(true);

    if (!password) {
      setError('password', {
        message: translate('errorProvidePassword'),
      });
      return;
    }

    try {
      const res =
        context && (await context.authentication.login(username, password));

      const {
        status,
        maskedIdentifier = '',
        factorId = '',
        mfaChallengeId = '',
      } = res as LoginResponse;

      // if successful log in
      if (status === 200) {
        return;
      }

      if (status === 204) {
        setMfaState({ factorId, mfaChallengeId, maskedIdentifier });
      }

      if (status === 235 && (res as LoginResponse).data?.session_id) {
        setTotpState({
          sessionId: (res as LoginResponse).data.session_id,
          isSetup: false,
        });
        return;
      }

      if (
        status === 202 &&
        (res as LoginResponse).data?.status === 'RESET_REQUIRED'
      ) {
        setResetRequiredState({
          email: username,
        });
      }

      if (status === 240 && (res as LoginResponse).data?.session_id) {
        setRegisterState({
          email: username,
          sessionId: (res as LoginResponse).data?.session_id,
        });
      }

      if (status === 250 && (res as LoginResponse).data?.session_id) {
        const responseData = (res as LoginResponse).data;
        setTotpSetupState({
          email: username,
          sessionId: responseData.session_id,
          secretCode: responseData.secret_code,
        });
      } else {
        throw new Error(translate('errorNetwork'));
      }
    } catch (err: any) {
      if (err.code === 401) {
        setError('password', {
          message: translate('errorIncorrectUsername/Password'),
        });
      } else {
        setError('root', { message: err.message });
      }
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (step === 'email') {
      await handleEmailStep(data.username);
    } else {
      await handlePasswordStep(data);
    }
  };

  const handleCancel = () => {
    setStep('email');
    setPasswordStepSubmitted(false);
    form.resetField('password');
    form.reset(form.getValues());
  };

  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const isLoading = step === 'email' ? isDiscovering : isSubmitting;

  const overridePasswordStateBeforeSubmit = passwordStepSubmitted
    ? undefined
    : 'default';

  return (
    <div className="flex w-full max-w-[400px] flex-col items-center gap-4">
      <Card
        className="relative w-full overflow-hidden rounded-xl p-0 shadow-md"
        ref={setContainer}
      >
        <div className="flex justify-center border-b border-border bg-card-secondary p-2">
          <RegionSelect container={container} />
        </div>
        <div className="flex flex-col gap-6 p-8 pt-6">
          <h1 className="text-center text-2xl font-medium text-foreground">
            {translate('login')}
          </h1>
          {timedOut && (
            <Notice intent="info">
              <Notice.Content>
                <Notice.Icon />
                <Notice.Caption>{translate('timedOut')}</Notice.Caption>
              </Notice.Content>
            </Notice>
          )}
          {hasSsoError && (
            <Notice intent="error">
              <Notice.Content>
                <Notice.Icon />
                <Notice.Caption>{translate('errorSsoExchange')}</Notice.Caption>
              </Notice.Content>
            </Notice>
          )}
          {errors.root && (
            <Notice intent="error">
              <Notice.Content>
                <Notice.Icon />
                <Notice.Caption>{errors.root.message}</Notice.Caption>
              </Notice.Content>
            </Notice>
          )}
          <Form {...form}>
            <form
              className="flex flex-col gap-6"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <Form.Field
                control={form.control}
                name="username"
                render={({ field }) => (
                  <Form.Item>
                    <Form.Label>{translate('username')}</Form.Label>
                    <Form.Control>
                      <Input
                        size="xl"
                        {...field}
                        disabled={isLoading || step === 'password'}
                      />
                    </Form.Control>
                    <Form.Message className="text-left" />
                  </Form.Item>
                )}
              />
              {step === 'password' && (
                <Form.Field
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <Form.Item>
                      <Form.Label>{translate('password')}</Form.Label>
                      <Form.Control>
                        <Input
                          size="xl"
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          disabled={isSubmitting}
                          autoFocus
                          data-state={overridePasswordStateBeforeSubmit}
                        />
                      </Form.Control>
                      <Form.Message
                        className="text-left"
                        state={overridePasswordStateBeforeSubmit}
                      />
                      <Checkbox id="show-password">
                        <Checkbox.Indicator
                          checked={showPassword}
                          disabled={isSubmitting}
                          onCheckedChange={(checked: boolean) => {
                            setShowPassword(checked);
                          }}
                        />
                        <Checkbox.Label>
                          {translate('showPassword')}
                        </Checkbox.Label>
                      </Checkbox>
                    </Form.Item>
                  )}
                />
              )}
              <Button
                className="w-full items-center justify-center"
                loading={isLoading}
                size="lg"
                type="submit"
              >
                <Button.Label>
                  {step === 'email'
                    ? translate('continue')
                    : translate('login')}
                </Button.Label>
              </Button>
            </form>
          </Form>
          {step === 'password' && (
            <TextLink asChild className="w-full items-center justify-center">
              <Link to="/login/forgot-password" aria-disabled={isSubmitting}>
                <TextLink.Label>{translate('forgotPassword')}</TextLink.Label>
              </Link>
            </TextLink>
          )}
        </div>
      </Card>
      {step === 'password' && (
        <TextLink asChild>
          <button type="button" onClick={handleCancel} disabled={isSubmitting}>
            <TextLink.Label>{translate('cancel')}</TextLink.Label>
          </button>
        </TextLink>
      )}
    </div>
  );
}
